import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import styles from "../../styles/button/mainButton.module.css";
import { HTMLAttributeAnchorTarget } from "react";

interface MainButtonProps {
  type?: "finance" | "primary" | "secondary" | "ghost" | "ghostOutlined" | "error" | "submit";
  to?: string | undefined;
  disabled?: boolean;
  customClass?: string;
  isSubmit?: boolean;
  passHref?: boolean;
  passHrefTarget?: HTMLAttributeAnchorTarget | undefined;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const MappedTypes: { [key: string]: string } = {
  finance: `${styles.base} ${styles.finance}`,
  primary: `${styles.base} ${styles.primary}`,
  secondary: `${styles.base} ${styles.secondary}`,
  ghost: `${styles.base} ${styles.ghost}`,
  ghostOutlined: `${styles.base} ${styles.ghostOutlined}`,
  error: `${styles.base} ${styles.error}`,
  submit: `${styles.base} ${styles.active}`,
};

const MainButton = ({ type = "finance", to = undefined, disabled = false, onClick, customClass, isSubmit, passHref, passHrefTarget, children }: MainButtonProps) => {
  if (disabled)
    return (
      <button disabled={true} className={`${styles.base} ${customClass ? customClass : ""}`} onClick={onClick ? onClick : undefined}>
        {children}
      </button>
    );

  if (type === "finance" && to) {
    return (
      <Link to={to}>
        <FinanceButton customClass={customClass} onClick={onClick ? onClick : undefined}>
          {children}
        </FinanceButton>
      </Link>
    );
  } else if (type === "finance" && !to) {
    return (
      <FinanceButton customClass={customClass} onClick={onClick ? onClick : undefined}>
        {children}
      </FinanceButton>
    );
  }

  if (passHref && to)
    return (
      <a href={to} target={passHrefTarget}>
        <button disabled={disabled} className={`${MappedTypes[type]} ${customClass ? customClass : ""}`} onClick={onClick ? onClick : undefined}>
          {children}
        </button>
      </a>
    );

  if (to)
    return (
      <Link to={to}>
        <button disabled={disabled} className={`${MappedTypes[type]} ${customClass ? customClass : ""}`} onClick={onClick ? onClick : undefined}>
          {children}
        </button>
      </Link>
    );

  return (
    <button type={isSubmit ? "submit" : undefined} disabled={disabled} className={`${MappedTypes[type]} ${customClass ? customClass : ""}`} onClick={onClick ? onClick : undefined}>
      {children}
    </button>
  );
};

/*
 * FinanceButton component
 * Renders a button with a gradient background animation
 * - Additional custom classes to apply to the button
 * - The function to be called when the button is clicked
 * - The content to be displayed inside the button
 * - The rendered FinanceButton component
 */
const FinanceButton = ({ customClass, onClick, children }: { customClass?: string; onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; children: React.ReactNode }) => {
  return (
    <motion.button
      className={`${MappedTypes["finance"]} ${customClass ? customClass : ""}`}
      initial={{ background: "linear-gradient(90deg, #474BCF 99.41%, #242669 112.34%)" }}
      whileHover={{
        background: "linear-gradient(94deg, #242669 0%, #474BCF 99.99%)",
        transition: { duration: 0.6 },
      }}
      whileTap={{
        background: "linear-gradient(274deg, #242669 0%, #474BCF 99.99%)",
        transition: { duration: 0.6 },
      }}
      onClick={onClick ? onClick : undefined}
    >
      {children}
    </motion.button>
  );
};

export default MainButton;
